import { createContext, ReactNode, useEffect, useReducer } from 'react';
import axios from '../utils/axios';
import {
  clearSetTimeout,
  getUserIdFromAuthToken,
  handleRefreshToken,
  isValidToken,
  setSession,
} from '../utils/jwt';
import { ActionMap, AuthState, AuthUser, JWTContextType } from '../@types/auth';
import { SaveUserDto } from 'src/@types/user';
import { userApi } from 'src/redux/api/userAPI';
import { useAppDispatch } from 'src/redux/hook';
import { userOrganizersActions } from 'src/redux/user-organizers';
import { userRolesActions } from 'src/redux/user-roles';
import { locationsActions } from 'src/redux/locations';
import { Language } from 'src/@types/location';
import { organizerSettingsActions } from 'src/redux/organizer-settings';
import { PaymentMethod } from 'src/@types/box-office';
import { appEnvVariables } from 'src/config';

enum Types {
  Initial = 'INITIALIZE',
  Login = 'LOGIN',
  Logout = 'LOGOUT',
  Register = 'REGISTER',
}

type JWTAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
  [Types.Login]: {
    user: AuthUser;
  };
  [Types.Logout]: undefined;
  [Types.Register]: {
    user: AuthUser;
  };
};

export type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
      };
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };

    case 'REGISTER':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };

    default:
      return state;
  }
};

const AuthContext = createContext<JWTContextType | null>(null);

type AuthProviderProps = {
  children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(JWTReducer, initialState);

  const reduxDispatch = useAppDispatch();

  const { setCurrentUserOrganizer } = userOrganizersActions;
  const { setCurrentUserRole, setPreviousRoles } = userRolesActions;
  const { setLocations } = locationsActions;
  const { setOrganizerSettings } = organizerSettingsActions;

  useEffect(() => {
    const initialize = async () => {
      try {
        let accessToken = localStorage.getItem('accessToken');
        let refreshToken = localStorage.getItem('refreshToken');

        if (refreshToken) {
          await handleRefreshToken({ refreshToken });

          accessToken = localStorage.getItem('accessToken');
          refreshToken = localStorage.getItem('refreshToken');
        }

        if (refreshToken && accessToken && isValidToken(accessToken)) {
          const id = getUserIdFromAuthToken(accessToken);

          const { data: user } = await axios.get(`user/${id}`);

          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: true,
              user,
            },
          });

          try {
            const { data: locations } = await axios.get('location');

            reduxDispatch(setLocations(locations));
          } catch (error) {
            console.error(error);
          }
        } else {
          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, [reduxDispatch, setLocations]);

  const login = async (email: string, password: string, rememberMe: boolean, userAgent: string) => {
    const loginResponse = await axios.post('user/login', {
      email,
      password,
      rememberMe,
      userAgent,
    });

    const { accessToken, refreshToken } = loginResponse.data;

    setSession(accessToken, refreshToken);
    localStorage.setItem('rememberMe', rememberMe.toString());

    const id = getUserIdFromAuthToken(accessToken);

    const { data: user } = await axios.get(`user/${id}`);

    dispatch({
      type: Types.Login,
      payload: {
        user,
      },
    });

    const { data: locations } = await axios.get('location');

    reduxDispatch(setLocations(locations));
  };

  const register = async (params: {
    saveUserDto: SaveUserDto;
    language: Language;
    token?: string;
  }) => {
    const { saveUserDto, language, token } = params;

    const response = await axios.put('user', saveUserDto, {
      params: {
        token,
      },
      headers: {
        'Accept-Language': language,
      },
    });

    const { accessToken, user } = response.data;

    localStorage.setItem('accessToken', accessToken);

    dispatch({
      type: Types.Register,
      payload: {
        user,
      },
    });

    return response.data;
  };

  // FIXME missing authentication check
  const setUser = async (user: AuthUser) => {
    dispatch({
      type: Types.Initial,
      payload: {
        isAuthenticated: true,
        user,
      },
    });
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('refreshToken');

    let userId = '';

    try {
      if (refreshToken) {
        userId = getUserIdFromAuthToken(refreshToken);

        await axios.post('user/logout', { userId, refreshToken });
      }
    } catch (error) {
      console.error(error);
    }

    dispatch({ type: Types.Logout });

    setTimeout(() => {
      reduxDispatch(setCurrentUserOrganizer(null));
      reduxDispatch(setCurrentUserRole(null));
      reduxDispatch(setPreviousRoles([]));
      reduxDispatch(
        setOrganizerSettings({
          settingsId: null,
          paymentMethods: [PaymentMethod.CASH, PaymentMethod.CARD],
        })
      );
      reduxDispatch(userApi.util.resetApiState());
    }, 100);

    setSession(null);
    clearSetTimeout();

    window.location.href = `${appEnvVariables.padminHost}${window.location.pathname}`;
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
